import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Button, Flex } from "rebass";
import { Input } from "@rebass/forms";
import { debounce, filter } from "lodash";
import { AiOutlineMinusCircle, AiOutlinePlusCircle } from "react-icons/ai";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { yodaOrdersActions } from "../../logic/actions";
import config from "../../utils/globalConfig";
import { ToastMessage } from "../order/ToastMessage";
import useResponsive from "../../hooks/useResponsive";
import { getLocalizedString, theme } from "../../utils";
import { isInViewport } from "../../utils/isInViewport";
import { FaUndoAlt } from "react-icons/fa";
import Select, { components } from "react-select";
import { useMemo } from "react";
import { BsCheck2 } from "react-icons/bs";

const NumericPad = ({
  id,
  quantity,
  sizeCode,
  door,
  color,
  type,
  forceLineId,
  index,
  editingMobile,
  setEditingMobile,
  isCart,
}) => {
  const ref = useRef();
  const containerRef = useRef();
  const arrowsRef = useRef();
  const mobileConfirmRef = useRef();
  const dispatch = useDispatch();
  const { orderId } = useParams();
  const [value, setValue] = useState(0);
  const [tmpValue, setTmpValue] = useState(0);
  const [orderRow, setOrderRow] = useState(null);
  const [message, setMessage] = useState(null);
  const [identifier, setIdentifier] = useState(null);
  const [editing, setEditing] = useState(false);
  const { catalogueItemDetails, catalogueItemDetailsSwap, catalogue } =
    useSelector((state) => state.yodaCatalogue);
  const { orderRowDetails } = useSelector((state) => state.yodaOrders);
  const { me } = useSelector((state) => state.yodaStakeholders);

  const { isMobile, type: deviceType } = useResponsive();

  const isEditing = editing || editingMobile === sizeCode;

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target) &&
        arrowsRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        if (isEditing) setEditing(false);
      }
    };
    window.addEventListener("click", handleOutsideClick);
    return () => {
      window.removeEventListener("click", handleOutsideClick);
    };
  }, [containerRef, arrowsRef, isEditing]);

  useEffect(() => {
    let tmpArray = ref.current.className
      .split(" ")
      .find((f) => f.match("_"))
      .split("_");
    tmpArray.pop();
    tmpArray.push(index);
    tmpArray = tmpArray.join("_");

    function getAutocancelledModel() {
      if (quantity < 1) {
        return;
      }
      return getGenerateUpdateModel(color, 0);
    }

    if (document && tmpArray) {
      document.externalPinPadHandle[tmpArray] = {
        getUpdateModel: getUpdateModel,
        getAutocancelledModel: getAutocancelledModel,
      };
    }

    window.addEventListener("numericPad" + tmpArray, testEvent);
    window.addEventListener("keydown", testEvent);
    setIdentifier(tmpArray);

    return () => {
      window.removeEventListener("numericPad" + tmpArray, testEvent);
      window.removeEventListener("keydown", testEvent);
    };
  }, [getUpdateModel, index]);

  function testEvent(e) {
    setTimeout(() => {
      setMessage(e.detail?.message);
      if (e.detail.clearValues) {
        setTmpValue(Number(e.detail.quantity));
        setValue(Number(e.detail.quantity));
      }
    }, 500);
  }

  useEffect(() => {
    let catalogId = undefined;
    let rowId = undefined;
    if (forceLineId !== undefined) {
      rowId = forceLineId;
    } else {
      catalogId =
        catalogueItemDetails[id]?.catalog?.filter(
          (f) => f?.sizeRowId == sizeCode && f?.colorCode === color,
        )[0]?.catalogId ||
        catalogueItemDetailsSwap[id]?.catalog?.filter(
          (f) => f?.sizeRowId == sizeCode && f?.colorCode === color,
        )[0]?.catalogId ||
        catalogue
          ?.filter((f) => f?.productId == id)[0]
          ?.catalog?.filter(
            (f) => f?.sizeRowId == sizeCode && f?.colorCode === color,
          )[0]?.catalogId;
      rowId = filter(
        orderRowDetails,
        (k, v) =>
          k.catalogId == catalogId &&
          k.sizeRowId == sizeCode &&
          k.customData.doorId == door &&
          k.customData.typeId == type,
      )[0]?.id;
    }
    if (rowId !== undefined && orderRowDetails[rowId] !== undefined) {
      setOrderRow(rowId);
    }
  }, [
    catalogue,
    catalogueItemDetails,
    catalogueItemDetailsSwap,
    door,
    forceLineId,
    id,
    orderRowDetails,
    sizeCode,
    type,
    color,
  ]);

  useEffect(() => {
    let catalogId = undefined;
    let rowId = undefined;

    if (forceLineId !== undefined) {
      rowId = forceLineId;
    } else {
      catalogId =
        catalogueItemDetails[id]?.catalog?.filter(
          (f) => f?.sizeRowId == sizeCode && f?.colorCode === color,
        )[0]?.catalogId ||
        catalogueItemDetailsSwap[id]?.catalog?.filter(
          (f) => f?.sizeRowId == sizeCode && f?.colorCode === color,
        )[0]?.catalogId ||
        catalogue
          ?.filter((f) => f?.productId == id)[0]
          ?.catalog?.filter((f) => {
            return f?.sizeRowId == sizeCode && f?.colorCode === color;
          })[0]?.catalogId;
      rowId = filter(
        orderRowDetails,
        (k, v) =>
          k.catalogId == catalogId &&
          k.sizeRowId == sizeCode &&
          k.customData.doorId == door &&
          k.customData.typeId == type,
      )[0]?.id;
    }
    if (rowId !== undefined && orderRowDetails[rowId] !== undefined) {
      setOrderRow(rowId);
      setValue(orderRowDetails[rowId].quantity);
      setTmpValue(orderRowDetails[rowId].quantity);
    } else {
      setValue(0);
      setTmpValue(0);
    }
  }, [
    catalogue,
    catalogueItemDetails,
    catalogueItemDetailsSwap,
    door,
    forceLineId,
    id,
    orderRowDetails,
    sizeCode,
    type,
    color,
    index,
  ]);

  function enable() {
    if (quantity > 0) {
      setEditing(true);
    }
  }

  function onSave(value, color, orderRow, orderId, tmpValue, identifier, ref) {
    setEditingMobile?.(null);
    setEditing(false);
    if (value !== "") {
      if (config.CONCURRENT_ORDER_API) {
        generateUpdateModel(color, value, identifier);
      } else {
        if (value > 0) {
          if (orderRow !== undefined && orderRow) {
            generateUpdateModel(color, value, identifier);
          } else {
            generateAddModel(color, value, identifier);
          }
        } else {
          if (orderRow !== undefined && orderRow) {
            dispatch(yodaOrdersActions.removeRow(orderId, orderRow));
          }
        }
      }
      ref?.current?.blur();
    } else {
      setValue(tmpValue);
    }
  }

  const handleSave = (passValue) => {
    onSave(
      passValue !== undefined ? passValue : value,
      color,
      orderRow,
      orderId,
      tmpValue,
      identifier,
      ref,
    );
  };

  const debounceSave = useCallback(
    debounce(
      (value, color, orderRow, orderId, tmpValue, identifier, ref) =>
        onSave(value, color, orderRow, orderId, tmpValue, identifier, ref),
      1200,
    ),
    [],
  );

  function disable(e) {
    const value = e.target.value;
    onSave(
      value === "" ? value : Number(value),
      color,
      orderRow,
      orderId,
      tmpValue,
    );
  }

  const handleUndo = () => {
    setValue(tmpValue);
    setEditingMobile?.(null);
  };

  function inputChange(e) {
    setValue(e.target.value);
    debounceSave(
      e.target.value,
      color,
      orderRow,
      orderId,
      tmpValue,
      identifier,
      ref,
    );
  }

  function inputChangeMobile(e) {
    if (!e.target.value) {
      setValue("0");
    }
    setValue(e.target.value);
    setEditingMobile?.(sizeCode);
  }

  function valueUp() {
    if (isMobile && quantity < 1) return;
    if (isMobile) {
      setEditingMobile?.(sizeCode);
      if (isCart && !isInViewport(mobileConfirmRef)) {
        mobileConfirmRef?.current?.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      }
    }
    const newValue = Number(value) + 1;
    setValue(newValue);
    if (!isMobile)
      debounceSave(
        newValue,
        color,
        orderRow,
        orderId,
        tmpValue,
        identifier,
        ref,
      );
  }

  function valueDown() {
    if (isMobile && quantity < 1) return;
    if (isMobile) {
      setEditingMobile?.(sizeCode);
      if (isCart && !isInViewport(mobileConfirmRef)) {
        mobileConfirmRef?.current?.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      }
    }
    const newValue = Number(value) == 0 ? 0 : Number(value) - 1;
    setValue(newValue);
    if (!isMobile)
      debounceSave(
        newValue,
        color,
        orderRow,
        orderId,
        tmpValue,
        identifier,
        ref,
      );
    // if (isMobile) return;
  }

  function generateAddModel(colorCode, val, identifier) {
    let objectDate = new Date();

    let day = objectDate.getDate() + "";
    let month = objectDate.getMonth() + 1 + "";
    let year = objectDate.getFullYear() + "";
    let date = year + "-" + month.padStart(2, "0") + "-" + day.padStart(2, "0");

    let catalogId = undefined;
    if (forceLineId !== undefined) {
      catalogId = filter(
        orderRowDetails,
        (k, v) => k.id == forceLineId && k.customData.typeId == type,
      )[0].catalogId;
    } else {
      catalogId =
        catalogueItemDetails[id]?.catalog?.filter(
          (f) => f?.sizeRowId == sizeCode && f?.colorCode == colorCode,
        )[0]?.catalogId ||
        catalogueItemDetailsSwap[id]?.catalog?.filter(
          (f) => f?.sizeRowId == sizeCode && f?.colorCode == colorCode,
        )[0]?.catalogId ||
        catalogue
          ?.filter((f) => f?.productId == id)[0]
          ?.catalog?.filter(
            (f) => f?.sizeRowId == sizeCode && f?.colorCode == colorCode,
          )[0]?.catalogId;
    }

    if (config.CONCURRENT_ORDER_API) {
      let model = {
        catalogId: catalogId,
        sizeRowId: Number(sizeCode),
        quantity: Number(val),
        customData: {
          typeId: type,
          doorId: door,
        },
      };

      dispatch(
        yodaOrdersActions.concurrentUpdateRow(
          orderId,
          model,
          index,
          val,
          identifier,
          Number(sizeCode),
        ),
      );
    }

    if (!config.CONCURRENT_ORDER_API) {
      let model = {
        customData: {
          typeId: type,
          doorId: door,
        },
        agent1Id: me.id,
        catalogId: catalogId,
        dateFrom: date,
        dateTo: date,
        unitId: 1,
        VatId: 1,
        orderRowsDetail: [
          {
            quantity: val,
            sizeRowId: Number(sizeCode),
          },
        ],
      };

      dispatch(
        yodaOrdersActions.addRow(orderId, model, index, val, identifier),
      );
    }
  }

  function getUpdateModel(val) {
    if (value > 0) {
      return;
    }

    if (quantity < 1) {
      return;
    }
    return getGenerateUpdateModel(color, val);
  }

  function getGenerateUpdateModel(colorCode, val) {
    if (orderRowDetails[orderRow] == undefined) {
      return getGenerateAddModel(colorCode, val);
    } else {
      let objectDate = new Date();

      let day = objectDate.getDate() + "";
      let month = objectDate.getMonth() + 1 + "";
      let year = objectDate.getFullYear() + "";
      let date =
        year + "-" + month.padStart(2, "0") + "-" + day.padStart(2, "0");

      let catalogId = undefined;
      if (forceLineId !== undefined) {
        catalogId = filter(
          orderRowDetails,
          (k, v) => k.id == forceLineId && k.customData.typeId == type,
        )[0].catalogId;
      } else {
        catalogId =
          catalogueItemDetails[id]?.catalog?.filter(
            (f) => f?.sizeRowId == sizeCode && f?.colorCode == colorCode,
          )[0]?.catalogId ||
          catalogueItemDetailsSwap[id]?.catalog?.filter(
            (f) => f?.sizeRowId == sizeCode && f?.colorCode == colorCode,
          )[0]?.catalogId ||
          catalogue
            ?.filter((f) => f?.productId == id)[0]
            ?.catalog?.filter(
              (f) => f?.sizeRowId == sizeCode && f?.colorCode == colorCode,
            )[0]?.catalogId;
      }

      if (config.CONCURRENT_ORDER_API) {
        let model = {
          catalogId: catalogId,
          sizeRowId: Number(sizeCode),
          quantity: Number(val),
          customData: {
            typeId: type,
            doorId: door,
          },
        };
        return {
          orderId,
          model,
          index,
          val,
          identifier: identifier,
          sizeCode: Number(sizeCode),
        };
      }
      if (!config.CONCURRENT_ORDER_API) {
        let model = {
          customData: {
            typeId: type,
            doorId: door,
          },
          agent1Id: me.id,
          catalogId: catalogId,
          dateFrom: date,
          dateTo: date,
          unitId: 1,
          VatId: 1,
          orderRowsDetail: [
            {
              quantity: val,
              sizeRowId: Number(sizeCode),
            },
          ],
        };

        return {
          orderId,
          model,
          index,
          val,
          identifier: identifier,
          sizeCode: Number(sizeCode),
        };
      }
    }
  }

  function getGenerateAddModel(colorCode, val) {
    let objectDate = new Date();

    let day = objectDate.getDate() + "";
    let month = objectDate.getMonth() + 1 + "";
    let year = objectDate.getFullYear() + "";
    let date = year + "-" + month.padStart(2, "0") + "-" + day.padStart(2, "0");

    let catalogId = undefined;
    if (forceLineId !== undefined) {
      catalogId = filter(
        orderRowDetails,
        (k, v) => k.id == forceLineId && k.customData.typeId == type,
      )[0].catalogId;
    } else {
      catalogId =
        catalogueItemDetails[id]?.catalog?.filter(
          (f) => f?.sizeRowId == sizeCode && f?.colorCode == colorCode,
        )[0]?.catalogId ||
        catalogueItemDetailsSwap[id]?.catalog?.filter(
          (f) => f?.sizeRowId == sizeCode && f?.colorCode == colorCode,
        )[0]?.catalogId ||
        catalogue
          ?.filter((f) => f?.productId == id)[0]
          ?.catalog?.filter(
            (f) => f?.sizeRowId == sizeCode && f?.colorCode == colorCode,
          )[0]?.catalogId;
    }

    if (config.CONCURRENT_ORDER_API) {
      let model = {
        catalogId: catalogId,
        sizeRowId: Number(sizeCode),
        quantity: Number(val),
        customData: {
          typeId: type,
          doorId: door,
        },
      };
      return {
        orderId,
        model,
        index,
        val,
        identifier: identifier,
        sizeCode: Number(sizeCode),
      };
    }

    if (!config.CONCURRENT_ORDER_API) {
      let model = {
        customData: {
          typeId: type,
          doorId: door,
        },
        agent1Id: me.id,
        catalogId: catalogId,
        dateFrom: date,
        dateTo: date,
        unitId: 1,
        VatId: 1,
        orderRowsDetail: [
          {
            quantity: val,
            sizeRowId: Number(sizeCode),
          },
        ],
      };

      dispatch(
        yodaOrdersActions.addRow(orderId, model, index, val, identifier),
      );
    }
  }

  function generateUpdateModel(colorCode, val, identifier) {
    if (orderRowDetails[orderRow] == undefined) {
      generateAddModel(colorCode, val, identifier);
    } else {
      let objectDate = new Date();

      let day = objectDate.getDate() + "";
      let month = objectDate.getMonth() + 1 + "";
      let year = objectDate.getFullYear() + "";
      let date =
        year + "-" + month.padStart(2, "0") + "-" + day.padStart(2, "0");

      let catalogId = undefined;
      if (forceLineId !== undefined) {
        catalogId = filter(
          orderRowDetails,
          (k, v) => k.id == forceLineId && k.customData.typeId == type,
        )[0].catalogId;
      } else {
        catalogId =
          catalogueItemDetails[id]?.catalog?.filter(
            (f) => f?.sizeRowId == sizeCode && f?.colorCode == colorCode,
          )[0]?.catalogId ||
          catalogueItemDetailsSwap[id]?.catalog?.filter(
            (f) => f?.sizeRowId == sizeCode && f?.colorCode == colorCode,
          )[0]?.catalogId ||
          catalogue
            ?.filter((f) => f?.productId == id)[0]
            ?.catalog?.filter(
              (f) => f?.sizeRowId == sizeCode && f?.colorCode == colorCode,
            )[0]?.catalogId;
      }

      if (config.CONCURRENT_ORDER_API) {
        let model = {
          catalogId: catalogId,
          sizeRowId: Number(sizeCode),
          quantity: Number(val),
          customData: {
            typeId: type,
            doorId: door,
          },
        };

        dispatch(
          yodaOrdersActions.concurrentUpdateRow(
            orderId,
            model,
            index,
            val,
            identifier,
            Number(sizeCode),
          ),
        );
      }

      if (!config.CONCURRENT_ORDER_API) {
        let model = {
          customData: {
            typeId: type,
            doorId: door,
          },
          agent1Id: me.id,
          catalogId: catalogId,
          dateFrom: date,
          dateTo: date,
          unitId: 1,
          VatId: 1,
          orderRowsDetail: [
            {
              quantity: val,
              sizeRowId: Number(sizeCode),
            },
          ],
          updated: orderRowDetails[orderRow].updated,
        };

        dispatch(
          yodaOrdersActions.updateRow(
            orderId,
            orderRow,
            model,
            index,
            val,
            identifier,
          ),
        );
      }
    }
  }

  const tabletOptions = useMemo(() => {
    const arrayLength =
      quantity < 10
        ? 20
        : quantity < 20
          ? 35
          : quantity < 50
            ? 55
            : quantity < 100
              ? 120
              : quantity + 8;
    let options = [];

    for (let i = 0; i <= arrayLength; i++) {
      options.push({ value: i, label: i });
    }
    return options;
  }, [quantity]);

  return !isMobile ? (
    <Flex
      ref={containerRef}
      onClick={() => {
        if (!deviceType.tablet) enable();
      }}
      sx={{
        height: "100%",
        flex: 1,
        ...(!quantity && { pointerEvents: "none" }),
      }}
      id={[id, color, door, index].join("_")}
    >
      <ToastMessage
        message={message}
        needConfirm={false}
        confirmFunction={() => {
          setMessage(null);
        }}
        style={{ width: 600 }}
      ></ToastMessage>
      {isEditing && (
        <Flex
          sx={{
            zIndex: 1,
            position: "absolute",
            left: "100%",
            marginTop: 0,
            height: "100%",
          }}
        >
          <Flex
            ref={arrowsRef}
            style={{ position: "relative", height: "100%" }}
            flexDirection={deviceType.tablet ? "row" : "column"}
            backgroundColor={deviceType.tablet ? theme.background : "#646464"}
            padding={deviceType.tablet ? "2px" : 0}
          >
            <Flex
              className="valueUp"
              onClick={valueUp}
              justifyContent={"center"}
              alignItems={"center"}
              sx={{
                cursor: "pointer",
                backgroundColor: "#646464",
                height: deviceType.tablet ? "100%" : "calc(50% - 1px)",
                width: deviceType.tablet ? "50px" : "15px",
                flex: 1,
                lineHeight: "15px",
                fontSize: "20px",
                "&:hover": {
                  opacity: ".8",
                },
              }}
            >
              <Box>+</Box>
            </Flex>
            {
              <Box
                sx={{
                  background: "white",
                  height: deviceType.tablet ? "100%" : "2px",
                  width: deviceType.tablet ? "2px" : "15px",
                }}
              />
            }
            <Flex
              className="valueDown"
              onClick={valueDown}
              justifyContent={"center"}
              alignItems={"center"}
              sx={{
                cursor: "pointer",
                backgroundColor: "#646464",
                height: deviceType.tablet ? "100%" : "calc(50% - 1px)",
                width: deviceType.tablet ? "50px" : "15px",
                flex: 1,
                lineHeight: "15px",
                fontSize: "20px",
                "&:hover": {
                  opacity: ".8",
                },
              }}
            >
              <Box>-</Box>
            </Flex>
          </Flex>
        </Flex>
      )}

      <Flex
        justifyContent={"center"}
        alignItems={"center"}
        width="100%"
        height="100%"
        onClick={() => {
          if (!deviceType.tablet) {
            setTimeout(() => {
              if (value === "0" || value === 0) setValue("");
              else if (value) {
                ref?.current?.select();
              }
              ref?.current?.focus();
            });
          }
        }}
      >
        {deviceType.tablet ? (
          <Box
            className={
              (quantity > 0 ? "editable " : " ") +
              "numericPadInput " +
              [id, color, door, value].join("_")
            }
            ref={ref}
            width="100%"
          >
            <Select
              unstyled
              captureMenuScroll
              maxMenuHeight="150px"
              menuShouldScrollIntoView
              styles={{
                control: (styles, state) => ({
                  ...styles,
                  border: "2px solid transparent",
                  boxShadow: "none",
                  width: "100%",
                  background: state.menuIsOpen ? "#707070" : "transparent",
                  pointerEvents: quantity > 0 ? "default" : "none",
                }),
                input: (styles) => ({
                  ...styles,
                  color: "#fff",
                }),
                menu: (styles) => ({
                  ...styles,
                  minWidth: "100%",
                  width: "fit-content",
                  overflow: "hidden",
                  display: "flex",
                  justifyContent: "center",
                  margin: "-32px 4px 0",
                }),
                menuList: (styles) => ({
                  ...styles,
                  width: "fit-content",
                  backgroundColor: "#fff",
                  borderRadius: "6px",
                  color: "black",
                  border: "1px solid black",
                  overflowY: "scroll",
                  py: "6px",
                }),
                option: (styles, state) => ({
                  ...styles,
                  padding: "8px 16px",
                  minWidth: "60px",
                }),
              }}
              name={`door${door}-size${sizeCode}`}
              placeholder={"-"}
              components={{
                IndicatorSeparator: () => null,
                DropdownIndicator: () => null,
                Option,
              }}
              onChange={(option) => {
                setValue(option?.value || 0);
                handleSave(option?.value || 0);
              }}
              value={
                tabletOptions.find((option) => option.value == value)?.value !==
                0
                  ? tabletOptions.find((option) => option.value == value)
                  : null
              }
              options={tabletOptions}
              isSearchable={false}
            />
          </Box>
        ) : (
          <>
            <Input
              type="text"
              inputmode="numeric"
              className={
                (quantity > 0 ? "editable " : " ") +
                "numericPadInput " +
                [id, color, door, value].join("_")
              }
              ref={ref}
              style={{
                borderColor: "#464646",
                display: isEditing ? "block" : "none",
                outline: "none",
                border: 0,
                textAlign: "center",
                padding: 0,
              }}
              value={value}
              onChange={inputChange}
              onBlur={() => {
                if (value === "") setValue(0);
              }}
            />
            <Box sx={{ display: isEditing ? "none" : "block" }}>
              {value === 0 ? "-" : value}
            </Box>
          </>
        )}
      </Flex>
    </Flex>
  ) : (
    <>
      <ToastMessage
        message={message}
        needConfirm={false}
        confirmFunction={() => {
          setMessage(null);
        }}
        style={{ width: 600 }}
      ></ToastMessage>
      <Flex sx={{ gap: "5px" }} flexGrow={1}>
        <Flex
          justifyContent={"center"}
          alignContent={"center"}
          backgroundColor={"white"}
          flexGrow={1}
        >
          <Input
            type="text"
            inputmode="numeric"
            ref={ref}
            className={
              (quantity > 0 ? "editable " : " ") +
              "numericPadInput " +
              [id, color, door, value].join("_")
            }
            sx={{
              borderColor: "#464646",
              outline: "none",
              border: 0,
              background: "none",
              padding: 0,
              textAlign: "center",
            }}
            value={value === 0 ? "-" : value}
            onChange={inputChangeMobile}
            // onTouchEnd={() => {
            //     if(value === 0){
            //         setValue('')
            //     }
            //     }}
            readOnly
            disabled={!quantity}
          ></Input>
        </Flex>
        {/*mobile */}
        <div className="valueUp" onClick={valueUp}>
          <AiOutlinePlusCircle color="white" size="25px" />
        </div>
        <div className="valueDown" onClick={valueDown}>
          <AiOutlineMinusCircle color="white" size="25px" />
        </div>
      </Flex>
      <Box
        ref={mobileConfirmRef}
        sx={{
          position: "absolute",
          width: "97%",
          right: 1,
          bottom: "112px",
          padding: "10px",
          visibility: isEditing ? "visible" : "hidden",
          pointerEvents: isEditing ? "default" : "none",
          bg: "#232323",
          zIndex: 1,
          border: "1px solid white",
          ...(isCart && {
            bottom: "unset",
            top: ref?.current?.offsetTop + 32,
          }),
        }}
      >
        <Flex
          width="100%"
          alignItems={"center"}
          justifyContent={"space-around"}
          sx={{ gap: "5px" }}
        >
          <Button
            variant="primary"
            sx={{
              fontFamily: "Open Sans",
              fontSize: "0.8em",
              fontWeigth: 800,
              color: "#000",
              width: "220px",
              background: "#fff",
              textTransform: "uppercase",
              flexGrow: 1,
            }}
            onClick={() => handleSave()}
          >
            {getLocalizedString("SAVE_UPDATE_MOBILE")}
          </Button>
          <Button
            sx={{
              padding: "8px",
            }}
            onClick={handleUndo}
          >
            <FaUndoAlt />
          </Button>
        </Flex>
      </Box>
    </>
  );
};

const Option = ({ children, ...rest }) => (
  <components.Option {...rest}>
    <Flex sx={{ gap: "8px", alignItems: "center", justifyContent: "center" }}>
      <Box
        width="18px"
        sx={{ visibility: !!rest.isSelected ? "visible" : "hidden" }}
      >
        <BsCheck2 size={18} />
      </Box>
      <Box>{children}</Box>
      <Box width="18px"></Box>
    </Flex>
  </components.Option>
);

export { NumericPad };
